export type GameModeId = "career" | "moving" | "daily";

export interface GameMode {
  id: GameModeId;
  name: string;
  eyebrow: string;
  description: string;
  ranked: boolean;
  movingRim: boolean;
  xpMultiplier: number;
}

export interface AthleteProfile {
  id: string;
  name: string;
  tagline: string;
  unlockTier: number;
  throwPower: number;
  gravity: number;
  size: number;
  accuracy: number;
}

export interface CareerSave {
  version: 1;
  xp: number;
  selectedProfile: string;
  selectedMode: GameModeId;
  playerName: string;
  crewCode: string;
  bests: Record<GameModeId, number>;
  dailyCompleted: string;
}

export const RULES_VERSION = 4;
export const ROUND_SECONDS = 60;

export const MODES: Record<GameModeId, GameMode> = {
  career: {
    id: "career",
    name: "Street Career",
    eyebrow: "BUILD YOUR LINEUP",
    description: "Earn rep, unlock athlete builds, and chase your own best.",
    ranked: false,
    movingRim: false,
    xpMultiplier: 1,
  },
  moving: {
    id: "moving",
    name: "Moving Rim",
    eyebrow: "GLOBAL RANKED",
    description: "Fixed lineup. Wind. Sliding basket. Global and crew boards.",
    ranked: true,
    movingRim: true,
    xpMultiplier: 1.5,
  },
  daily: {
    id: "daily",
    name: "Daily Run",
    eyebrow: "ONE SEEDED ATTEMPT",
    description: "The same court, wind, and lineup for everyone. Resets at 00:00 UTC.",
    ranked: true,
    movingRim: true,
    xpMultiplier: 2,
  },
};

export const TIERS = [
  { name: "Playground", minimumXp: 0 },
  { name: "Ghetto League", minimumXp: 250 },
  { name: "Plantation Pro Cup", minimumXp: 800 },
  { name: "NBA · Never Been Acquired", minimumXp: 1_900 },
] as const;

export const ATHLETE_PROFILES: readonly AthleteProfile[] = [
  {
    id: "rookie",
    name: "Rookie",
    tagline: "Balanced and leaderboard legal",
    unlockTier: 0,
    throwPower: 1,
    gravity: 1,
    size: 1,
    accuracy: 15,
  },
  {
    id: "flight-risk",
    name: "Flight Risk",
    tagline: "Lighter body, longer hang time",
    unlockTier: 1,
    throwPower: 1.04,
    gravity: 0.91,
    size: 0.92,
    accuracy: 14,
  },
  {
    id: "big-fella",
    name: "Big Fella",
    tagline: "Heavy arc, filthy rim impact",
    unlockTier: 2,
    throwPower: 0.9,
    gravity: 1.12,
    size: 1.16,
    accuracy: 18,
  },
  {
    id: "deadeye",
    name: "Deadeye",
    tagline: "Small perfect-shot window? Not anymore",
    unlockTier: 3,
    throwPower: 0.98,
    gravity: 0.98,
    size: 0.96,
    accuracy: 25,
  },
] as const;

const DEFAULT_SAVE: CareerSave = {
  version: 1,
  xp: 0,
  selectedProfile: "rookie",
  selectedMode: "career",
  playerName: "Rookie",
  crewCode: "",
  bests: { career: 0, moving: 0, daily: 0 },
  dailyCompleted: "",
};

export class SeededRandom {
  private state: number;

  constructor(seed: number) {
    this.state = seed >>> 0 || 0x6d2b79f5;
  }

  next(): number {
    let value = this.state;
    value ^= value << 13;
    value ^= value >>> 17;
    value ^= value << 5;
    this.state = value >>> 0;
    return this.state / 0x1_0000_0000;
  }
}

export function seedFromString(value: string): number {
  let hash = 2_166_136_261;
  for (let index = 0; index < value.length; index += 1) {
    hash ^= value.charCodeAt(index);
    hash = Math.imul(hash, 16_777_619);
  }
  return hash >>> 0;
}

export function randomSeed(): number {
  const values = new Uint32Array(1);
  crypto.getRandomValues(values);
  return values[0] ?? 1;
}

export function utcDay(date = new Date()): string {
  return date.toISOString().slice(0, 10);
}

export function pointsForDistance(releaseX: number, rimX: number): number {
  const distance = Math.abs(rimX - releaseX);
  if (distance >= 720) return 3;
  if (distance >= 430) return 2;
  return 1;
}

export function tierIndexForXp(xp: number): number {
  let tier = 0;
  for (let index = 1; index < TIERS.length; index += 1) {
    if (xp >= (TIERS[index]?.minimumXp ?? Number.POSITIVE_INFINITY)) tier = index;
  }
  return tier;
}

export function profileById(id: string): AthleteProfile {
  return ATHLETE_PROFILES.find((profile) => profile.id === id) ?? ATHLETE_PROFILES[0]!;
}

export function parseCareerSave(value: string | null, legacyBest = 0): CareerSave {
  if (!value) return { ...DEFAULT_SAVE, bests: { ...DEFAULT_SAVE.bests, career: legacyBest } };

  try {
    const parsed = JSON.parse(value) as Partial<CareerSave>;
    const mode = parsed.selectedMode;
    const selectedMode: GameModeId = mode === "moving" || mode === "daily" ? mode : "career";
    const bests = parsed.bests ?? DEFAULT_SAVE.bests;
    return {
      version: 1,
      xp: finiteNonNegative(parsed.xp),
      selectedProfile: typeof parsed.selectedProfile === "string" ? parsed.selectedProfile : "rookie",
      selectedMode,
      playerName: typeof parsed.playerName === "string" ? parsed.playerName.slice(0, 18) : "Rookie",
      crewCode: typeof parsed.crewCode === "string" ? parsed.crewCode.slice(0, 12) : "",
      bests: {
        career: Math.max(legacyBest, finiteNonNegative(bests.career)),
        moving: finiteNonNegative(bests.moving),
        daily: finiteNonNegative(bests.daily),
      },
      dailyCompleted: typeof parsed.dailyCompleted === "string" ? parsed.dailyCompleted : "",
    };
  } catch {
    return { ...DEFAULT_SAVE, bests: { ...DEFAULT_SAVE.bests, career: legacyBest } };
  }
}

function finiteNonNegative(value: unknown): number {
  return typeof value === "number" && Number.isFinite(value) && value >= 0 ? Math.floor(value) : 0;
}
